import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Avatar,
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const PHOTO_SIZE = 120;

const getPhotoSrc = (photo) => {
    if (!photo || typeof photo !== 'string') return undefined;
    const trimmed = photo.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('data:')) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
};

const StudentPhoto = ({
                          componentId,
                          field,
                          index,
                          onChange,
                          descriptionHtml,
                          onDescriptionHtmlChange,
                          commitDescriptionHtml,
                          isEditMode,
                          isFormArchived,
                          readOnly,
                          placeholderValues,
                          accessLevel,
                          audience,
                          recipientType,
                          linkedToStudents,
                          linkedToEmployees,
                          submitMode,
                      }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const alignment = field?.alignment || 'left';

    const photoSrc = useMemo(
        () => getPhotoSrc(placeholderValues?.studentPhoto),
        [placeholderValues?.studentPhoto]
    );

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const renderPhoto = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent:
                    alignment === 'center'
                        ? 'center'
                        : alignment === 'right'
                            ? 'flex-end'
                            : 'flex-start',
                py: 1,
            }}
        >
            <Avatar
                variant="square"
                src={photoSrc}
                sx={{
                    width: PHOTO_SIZE,
                    height: PHOTO_SIZE,
                    bgcolor: 'grey.200',
                }}
            />
        </Box>
    );

    if (isEditMode) {
        return (
            <Box sx={{mt: 2}}>

                <Box sx={{mb: 2}}>
                    <FormControl size="small" sx={{minWidth: 140}}>
                        <InputLabel>Alignment</InputLabel>
                        <Select
                            value={alignment}
                            label="Alignment"
                            onChange={(e) => onChange({index, alignment: e.target.value})}
                        >
                            <MenuItem value="left">Left</MenuItem>
                            <MenuItem value="center">Center</MenuItem>
                            <MenuItem value="right">Right</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <RichEditor
                    currentDescription={draftDescriptionHtml}
                    setCurrentDescription={handleDraftChange}
                    isFormLive={isFormArchived}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={linkedToStudents}
                    linkedToEmployees={linkedToEmployees}
                    placeholderValues={placeholderValues}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />

                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="caption" color="textSecondary" sx={{mb: 1, display: 'block'}}>
                        Preview:
                    </Typography>
                    {renderPhoto()}
                    {!photoSrc && (
                        <Typography variant="caption" color="textSecondary">
                            The linked student&apos;s profile photo will appear here when the form is opened.
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{mt: 2}}>
            <RichEditorResultText
                id={componentId}
                description={descriptionHtml}
                placeholderValues={placeholderValues}
                readOnly={readOnly}
                enableValidation={false}
                audience={audience}
                recipientType={recipientType}
                linkedToStudents={linkedToStudents}
                linkedToEmployees={linkedToEmployees}
                submitMode={submitMode}
            />
            {photoSrc && renderPhoto()}
        </Box>
    );
};

StudentPhoto.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    field: PropTypes.object,
    index: PropTypes.number,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool,
};

export default StudentPhoto;
